<template>
  <PagePortal
    v-if="!showInitialPageLoading"
    :title="currentCategoryMetaTitle || $t('general.cezma')"
    :description="currentCategoryMetaDescription"
    :imageAlt="currentCategoryImageAlt"
    :keywords="currentCategoryKeywords"
  >
    <div id="productsPage" class="filter-container max-w-[1920px] mb-20">
      <!-- SEO Content Section (above breadcrumb) -->
      <div
        v-if="currentCategorySeoContent"
        class="w-full flex justify-center items-center p-4"
      >
        <div class="text-center max-w-4xl">
          <div
            v-html="displayedSeoContent"
            class="ql-editor text-gray-700 text-base leading-relaxed inline"
            style="padding: 0; display: inline"
          ></div>
          <button
            v-if="shouldShowReadMore"
            @click="isSeoContentExpanded = !isSeoContentExpanded"
            class="text-primary hover:underline mx-1 font-medium cursor-pointer bg-transparent border-none"
          >
            {{
              isSeoContentExpanded
                ? $t("general.read_less")
                : $t("general.read_more")
            }}
          </button>
        </div>
      </div>
      <Breadcrumb :links="breadcrumbLinks" />
      <!-- FilterBar for mobile  -->
      <button
        @click="openFilter"
        class="fixed z-30 bg-primary bottom-[40px] right-[40px] p-[16px] rounded-lg flex justify-center items-center md:hidden"
      >
        <img
          class="w-[25px] h-[25px]"
          src="../../assets/icons/filter.svg"
          alt=""
        />
      </button>
      <div
        v-show="isPopup"
        class="fixed top-0 left-0 h-[100vh] w-[100vw] bg-white scrollable z-[1000000] md:hidden"
      >
          <FilterBar
            ref="filterBarMobile"
            :key="`filter-mobile-${encodedRouteFilterQuery || 'default'}`"
            :withAttribute="true"
            :portalPage="'products'"
            @closeFilter="closeFilter"
            @filterStateChanged="onFilterStateChanged"
            @handleQuery="handleQuery"
            @handleAttributes="handleAttributes"
            @handleCategoryId="handleCategoryId"
          />
      </div>
      <!-- FilterBar for any device except mobile -->
      <section class="flex gap-[30px] min-h-[70vh]">
        <div class="hidden md:block">
          <FilterBar
            ref="filterBarDesktop"
            :key="`filter-desktop-${encodedRouteFilterQuery || 'default'}`"
            :withAttribute="true"
            :portalPage="'products'"
            @filterStateChanged="onFilterStateChanged"
            @handleQuery="handleQuery"
            @handleAttributes="handleAttributes"
            @handleCategoryId="handleCategoryId"
          />
        </div>

        <div
          class="max-w-full md:max-w-[45%] lg:max-w-[60%] xl:max-w-[75%] flex-1"
        >
          <!-- search -->
          <section>
            <form
              :class="`
          flex items-center   
          sm:relative  sm:flex w-full sm:items-center sm:p-0 mb-4
          `"
              @submit.prevent="handleSearchSubmit"
            >
              <!-- clear search -->
              <button
                v-if="!searchInpt == ''"
                type="button"
                @click="clearSearch"
                class="absolute sm:left-12 left-16 ltr:left-auto sm:ltr:right-16 ltr:right-12 inline-flex items-center w-[15px] rounded-[5px] cursor-pointer"
              >
                <img
                  src="@/assets/navBar/burger-menu-close-blue.svg"
                  class="w-fit"
                  alt=""
                />
              </button>

              <!-- input search -->
              <input
                v-model.trim="searchInpt"
                type="text"
                class="w-full p-2.5 rounded-none border border-[#F1F1F1] rounded-r-[5px] ltr:rounded-r-none ltr:rounded-l-[5px] bg-[#fff] outline-none focus:border-2 focus:border-[#e1e3e6] block text-sm tex text-[#000000] placeholder:text-[#8c8c8c]"
                :placeholder="$t('general.search_placeholder')"
              />
              <!-- search button -->
              <button
                type="submit"
                class="inline-flex items-center w-[40px] min-w-[40px] p-2.5 text-sm text-gray-900 bg-[#195ff6] border-0 border-r-0 rounded-l-[5px] ltr:rounded-l-none ltr:rounded-r-[5px] cursor-pointer"
              >
                <img
                  src="@/assets/navBar/search-white.svg"
                  class="object-fit"
                  alt=""
                />
              </button>
            </form>
          </section>
          <!-- Exclusive Offers -->
          <section
            v-if="exclusiveOffersData.length > 0"
            class="w-full my-10 overflow-hidden"
          >
            <ExclusiveOffersSlider
              :isLoading="isLoadingExclusiveOffers"
              :exclusiveOffersList="exclusiveOffersData"
            />
            <GoogleAdsBanner v-if="decodeQueryObj?.category_id != '383'" />
          </section>
          <section :class="isLoading.more ? 'mb-1' : 'mb-7 md:mb-11 w-full'">
            <!-- products and products skeleton when results request  -->
            <HomeTrendyProducts
              :hasReachedFullPages="true"
              :isLoading="isLoading.results"
              :isLoadingMore="true"
              :productsList="productsResult"
              :fromFilterProducts="true"
              title=""
              @saveProductCardPosition="saveProductCardPosition"
              :portalPage="'products'"
              :encodedFilterQuery="encodedRouteFilterQuery"
              :searchTerm="searchInpt"
            />
          </section>

          <!-- no results  -->
          <section
            v-if="!productsResult.length && !isLoading.results"
            class="bg-[#ffffff00] mb-20 border-gray-200 flex flex-col items-center justify-center px-4 md:px-8 lg:px-24 py-8 rounded-lg"
          >
            <p
              class="text-6xl md:text-7xl lg:text-9xl font-bold tracking-wider text-gray-300"
            >
              <svg
                aria-hidden="true"
                class="mx-auto mb-4 text-gray-400 w-14 h-14"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </p>
            <p
              class="text-1xl md:text-2xl lg:text-3xl font-bold text-gray-500 mt-4"
            >
              {{ $t("general.no_results") }}
            </p>
            <button
              @click="$router.push('/')"
              class="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-gray-100 px-4 py-2 mt-6 rounded transition duration-150"
              :title="$t('general.back_home')"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fill-rule="evenodd"
                  d="M9.707 14.707a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 1.414L7.414 9H15a1 1 0 110 2H7.414l2.293 2.293a1 1 0 010 1.414z"
                  clip-rule="evenodd"
                ></path>
              </svg>
              <span>{{ $t("general.back_home") }}</span>
            </button>
          </section>

          <!-- products skeleton when load more    -->
          <HomeTrendyProducts
            :fromFilterProducts="true"
            :isLoading="isLoading.more"
            :skeletonNumbers="5"
            :productsList="[]"
            @saveProductCardPosition="saveProductCardPosition"
            :encodedFilterQuery="encodedRouteFilterQuery"
            :searchTerm="searchInpt"
          />

          <!-- Infinite Scroll for Products -->
          <InfiniteScroll
            v-if="!isLoading.results && productsResult.length > 0"
            :loadMore="loadMoreProducts"
            :isLoadingMore="isLoading.more"
            :hasReachedEnd="!allowToLoadMore"
            :isLoading="isLoading.results"
            :endMessage="$t('general.no_more_products')"
          >
            <!-- Products are already displayed above, this just handles the infinite scroll -->

            <!-- Custom end message -->
            <template #end-message>
              <div class="text-center py-8">
                <p class="text-gray-500 text-lg">
                  {{ $t("general.no_more_products") }}
                </p>
                <p class="text-gray-400 text-sm mt-2">
                  {{
                    $t("general.you_have_seen_all_products") ||
                    "You have seen all available products"
                  }}
                </p>
              </div>
            </template>
          </InfiniteScroll>

          <!-- Remove the old load more button section -->
          <!-- <section class="grid place-items-center mb-8" v-if="allowToLoadMore">
        <button
          @click="loadMoreProducts"
          :disabled="isLoading.more"
          :class="`mb-10 mt-5 flex items-center justify-center gap-2 overflow-hidden bg-[#0963FF] hover:bg-[#0963FF]/90 min-w-[110px] sm:min-w-[125px] md:min-w-[150px] text-[#fff] font-bold text-sm lg:text-[0.938rem] md:text-[0.938rem] 3xl:text-base px-5 sm:px-8 py-1.5 sm:py-2 rounded-[19px]`">
          {{ $t('general.more') }}
          <span v-if="isLoading.more" class="loader flex items-center justify-center px-2 rounded-md">
            <span class="w-6 aspect-square rounded-full border-4 border-[#d0cfcf] border-t-[#ffff] animate-spin"></span>
          </span>
        </button>
      </section> -->
        </div>
      </section>
    </div>
  </PagePortal>

  <!-- Loading State shown before PagePortal renders -->
  <div v-else id="productsPage" class="filter-container max-w-[1920px] mb-20">
    <div class="flex justify-center items-center min-h-[70vh]">
      <div
        class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"
      ></div>
    </div>
  </div>
</template>

<script>
import {
  PagePortal,
  Breadcrumb,
  HomeTrendyProducts,
  DualRangeSlider,
  ExclusiveOffersSlider,
} from "@/components";
import { parseQueryString } from "@/helpers";
import Multiselect from "vue-multiselect";
import "vue-multiselect/dist/vue-multiselect.css";
import { useGlobalStore } from "@/store/Modules/global";
import { mapState } from "pinia";
import FilterBar from "@/components/searchAndFilter/FilterBar.vue";
import InfiniteScroll from "@/components/common/InfiniteScroll.vue";
import { safeProductListCache } from "@/utils/productListCache";
import { globalMixin } from "@/mixins/global";
import GoogleAdsBanner from "@/components/generic/GoogleAdsBanner.vue";
import { useFilterManager } from "@/composables/useFilterManager";
import { useProductRestoration } from "@/composables/useProductRestoration";
import "@vueup/vue-quill/dist/vue-quill.snow.css";
export default {
  name: "products",
  mixins: [globalMixin],
  components: {
    PagePortal,
    Breadcrumb,
    Multiselect,
    DualRangeSlider,
    HomeTrendyProducts,
    FilterBar,
    ExclusiveOffersSlider,
    InfiniteScroll,
    GoogleAdsBanner,
  },
  setup() {
    definePageMeta({
      layout: "user",
    });

    const global = useGlobalStore();
    
    // Initialize filter manager
    const filterManager = useFilterManager({
      portalPage: 'products',
      enableUrlSync: true,
      debounceMs: 200,
    });
    
    // Initialize restoration manager
    const restorationManager = useProductRestoration();
    
    // Connect managers
    filterManager.setRestorationManager(restorationManager);
    
    return { 
      global,
      filterManager,
      restorationManager,
    };
  },
  data() {
    return {
      allowToLoadMore: true,
      isPopup: false,
      isLoadingExclusiveOffers: false,
      exclusiveOffersData: [],
      // Legacy state kept for backward compatibility during transition
      filterQuery: "",
      attributesQuery: "",
      categoryId: "",
      isLoading: {
        categories: false,
        results: true,
        more: false,
      },
      categorySeoContent: {
        content_ar: "",
        content_en: "",
        meta_title_ar: "",
        meta_title_en: "",
        meta_description_ar: "",
        meta_description_en: "",
        image_alt_text: "",
        meta_keywords: "",
      },
      isSeoContentExpanded: false,

      all: { id: "all", name_ar: "الكل", name_en: "All" },
      space: {
        from: "",
        to: "",
        min: 0,
        max: 100000000,
      },
      searchInpt: "",

      //--------------------------------------------------
      productsResult: [],

      //--------------------------------------------------
      currentPage: 1,

      isUnmounted: false,
      isMounted: false,
      cachedDataDisplayed: false,
      isInitializingFilters: true,
      initialProductsLoadTriggered: false,
      hasInitialProductsLoaded: false,
      hasUserInteractedWithFilters: false,
      previousSortBy: null, // Track previous sortBy to detect sorting changes
      sortBy: "date", // Default sort by date
      sortType: "desc", // Default sort type descending
      // Legacy flags - will be removed after full refactoring
      _isRestoringPosition: false,
      _pendingProductRestore: null,
      _restoreRetryCount: 0,
      _pendingAttributesRestore: null,
      _handlingQueryChange: false,
      _filterBarInitializing: false,
      _handleQueryDebounceTimer: null,
      _restorationRetryCount: 0, // Track restoration retry attempts
    };
  },
  async created() {
    // Skip cache operations and API calls on server-side
    if (process.server) {
      this.isInitializingFilters = false;
      return;
    }

    // Check if we have a valid product navigation context BEFORE clearing cache
    const productContext = this.getStoredProductNavigationContext();
    const hasValidProductContext = productContext && 
                                   productContext.portalPage === 'products' &&
                                   (Date.now() - (productContext.timestamp || 0)) < (60 * 60 * 1000); // Less than 1 hour old

    console.log("🗑️ Products page created - checking for product context before clearing cache");
    console.log("Has valid product context?", hasValidProductContext);

    // Debug: Check cache before clearing
    const cacheStatsBefore = safeProductListCache.getCacheStats();
    console.log("📊 Cache stats BEFORE clearing:", cacheStatsBefore);

    // If we have a valid product context, preserve product positions for restoration
    if (hasValidProductContext) {
      console.log("💾 Preserving product positions for restoration");
      console.log("Product context:", productContext);
      // Clear cache but preserve product positions for the specific context
      safeProductListCache.clearCachePreserveProductPositions(
        productContext.cacheKey,
        productContext.page
      );
    } else {
      // No valid context, clear everything
      console.log("🗑️ No valid product context - clearing all cache");
      safeProductListCache.clearAllCache();
    }

    // Debug: Check cache after clearing
    const cacheStatsAfter = safeProductListCache.getCacheStats();
    console.log("📊 Cache stats AFTER clearing:", cacheStatsAfter);

    // Debug: Verify cache state
    const allCacheAfterClear = safeProductListCache.getCache();
    console.log(
      "📂 All cache entries after clear:",
      Object.keys(allCacheAfterClear)
    );

    console.log("✅ Product cache cleared at products page creation");

    // Extract parameters from URL query
    const mainCategoryId = this.decodeQueryObj?.main_category_id;
    const categoryId = this.decodeQueryObj?.category_id;
    const sortBy = this.decodeQueryObj?.sort_by;
    const sortType = this.decodeQueryObj?.sort_type;
    const searchQuery = this.decodeQueryObj?.search;
    const fromHomepage = this.decodeQueryObj?.from === "list";

    // Restore search input from query parameters
    if (searchQuery) {
      this.searchInpt = searchQuery;
      // Update filter manager with search
      this.filterManager.updateFilterState('url', { search: searchQuery }, { skipNotification: true });
    }

    // Set category ID and filter query based on URL parameters
    if (mainCategoryId && !categoryId) {
      this.filterQuery = `category_id=${mainCategoryId}`;
      this.categoryId = mainCategoryId;
    } else if (mainCategoryId && categoryId) {
      this.filterQuery = `category_id=${categoryId}`;
      this.categoryId = categoryId;
    } else if (!mainCategoryId && !categoryId) {
      this.filterQuery = ``;
      this.categoryId = "0";
    }

    // Store sort parameters for later use
    if (sortBy) {
      this.sortBy = sortBy;
    }
    if (sortType) {
      this.sortType = sortType;
    }
    
    // Filter manager will restore from URL in mounted(), so we don't need to do it here

    // Fetch SEO content for the current category
    await this.getCategorySeoContent();

    // if(!sortBy && !sortType){
    //   this.filterQuery = `${this.filterQuery}&sort_by=date&sort_type=desc`
    // }

    // Check for homepage navigation FIRST before checking saved data
    if (fromHomepage) {
      this.clearSavedProductsData();
      this.cachedDataDisplayed = false; // Ensure fresh API call

      // Force fresh API call for homepage navigation
      await this.getResults("search", 1, false, true); // Force skip cache
      this.isInitializingFilters = false;
      return; // Exit early after API call
    }

    // Check for saved products page data (only if NOT from homepage)
    const savedData = this.savedProductspageData || {};

    if (
      savedData &&
      Object.keys(savedData).length > 0 &&
      savedData?.productsResult?.length > 0
    ) {
      // Display saved data immediately
      this.productsResult = savedData.productsResult || [];
      this.currentPage = parseInt(savedData.currentPage) || 1;
      this.allowToLoadMore = savedData.allowToLoadMore || false;
      this.isLoading.results = false;
      this.isLoading.more = false;
      
      // Restore query parameters from URL first (highest priority), then fall back to saved data
      // This ensures UI reflects the URL state when navigating back
      // CRITICAL: For product position restoration, we need to preserve the exact filter query
      // that was used when the product was clicked, so check product context first
      const productContext = this.getStoredProductNavigationContext();
      if (productContext && productContext.portalPage === 'products') {
        // If we have a product context, we need to restore filters to match the context's cache key
        // The context's cache key was generated with specific filters, so we need to match those
        console.log('🔍 Product context found during saved data restoration, preserving filter state');
        // Use saved data filters as they should match the context
        this.searchInpt = savedData.searchInpt || "";
        this.filterQuery = savedData.filterQuery || "";
        this.attributesQuery = savedData.attributesQuery || "";
        this.categoryId = savedData.categoryId || "";
        
        // Extract attributes from filterQuery if attributesQuery is not available
        // FilterBar might not see attributes in URL, so we need to restore them after it initializes
        let attributesToRestore = savedData.attributesQuery || "";
        if (!attributesToRestore && savedData.filterQuery) {
          // Parse attribute_ids[] from filterQuery string (e.g., "category_id=35&attribute_ids[]=174")
          const attributeMatches = savedData.filterQuery.match(/attribute_ids\[\]=(\d+)/g);
          if (attributeMatches && attributeMatches.length > 0) {
            // Extract just the IDs and join them (FilterBar expects comma-separated or specific format)
            const attributeIds = attributeMatches.map(match => match.match(/\d+/)[0]);
            attributesToRestore = attributeIds.join(',');
          }
        }
        
        // Store attributes for restoration after FilterBar initializes
        if (attributesToRestore) {
          this.restorationManager.setPendingAttributesRestore(attributesToRestore);
          console.log('💾 Stored attributes for later restoration:', attributesToRestore);
        }
      } else {
        // No product context, use URL parameters first
        this.searchInpt = searchQuery || savedData.searchInpt || "";
        this.filterQuery = savedData.filterQuery || "";
        this.attributesQuery = savedData.attributesQuery || "";
        this.categoryId = savedData.categoryId || "";
      }
      
      // Restore sort parameters from URL first, then saved data
      if (sortBy) {
        this.sortBy = sortBy;
      }
      if (sortType) {
        this.sortType = sortType;
      }

      // Fetch SEO content for the restored category
      await this.getCategorySeoContent();

      // Mark that we've displayed saved data to prevent override
      this.cachedDataDisplayed = true;
      this.initialProductsLoadTriggered = true;
      this.isInitializingFilters = false;
      
      // CRITICAL: Update route query parameters IMMEDIATELY before FilterBar initializes
      // This ensures FilterBar component receives the correct query parameters when it mounts
      // We need to update the route synchronously so FilterBar can read it during initialization
      if (process.client) {
        // Parse filterQuery to extract ALL parameters dynamically
        // This ensures we capture every filter parameter, not just specific ones
        const filterParams = {};
        const attributeIds = [];
        
        if (this.filterQuery) {
          const params = this.filterQuery.split('&');
          params.forEach(param => {
            if (!param || !param.includes('=')) return;
            
            const [key, value] = param.split('=');
            if (key && value !== undefined && value !== '') {
              // Handle attribute_ids[] array parameters
              if (key === 'attribute_ids[]' || key.startsWith('attribute_ids[')) {
                attributeIds.push(decodeURIComponent(value));
              } else {
                // Store all other parameters (rating, is_negotiable, product_type, etc.)
                filterParams[key] = decodeURIComponent(value);
              }
            }
          });
        }
        
        // Store attribute_ids separately
        if (attributeIds.length > 0) {
          filterParams.attribute_ids = attributeIds;
        }
        
        
        // Build query parameters from restored filter state
        const queryObj = {};
        
        // Add category parameters
        if (this.categoryId && this.categoryId !== "0") {
          // Check if it's a main or sub category (we'll use main_category_id for now)
          // The actual category structure will be determined by FilterBar
          queryObj.main_category_id = this.categoryId;
        }
        
        // Add search
        if (this.searchInpt) {
          queryObj.search = this.searchInpt;
        }
        
        // Add sort parameters (from URL first, then filterQuery, then saved data)
        const sortBy = this.sortBy || filterParams.sort_by;
        const sortType = this.sortType || filterParams.sort_type;
        if (sortBy && sortBy !== "date") {
          queryObj.sort_by = sortBy;
        }
        if (sortType && sortType !== "desc") {
          queryObj.sort_type = sortType;
        }
        
        // Copy ALL filter parameters from filterQuery to queryObj
        // This includes: rating, is_negotiable, product_type, min_price, max_price, 
        // min_space, max_space, governorate_id, area_id, store_id, sort_by_force, etc.
        Object.keys(filterParams).forEach(key => {
          // Skip category_id as it's handled separately above
          // Skip attribute_ids as it's handled separately below
          if (key !== 'category_id' && key !== 'attribute_ids' && filterParams[key] !== undefined && filterParams[key] !== null && filterParams[key] !== '') {
            queryObj[key] = filterParams[key];
          }
        });
        
        
        // Build query parts array for proper encoding
        const queryParts = [];
        
        // Add category parameters
        Object.entries(queryObj).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            queryParts.push(`${key}=${encodeURIComponent(value)}`);
          }
        });
        
        // Add attribute IDs if available (from attributesQuery or filterQuery)
        let attrIds = [];
        if (this.attributesQuery) {
          if (typeof this.attributesQuery === 'string') {
            // Parse attribute IDs from attributesQuery string
            attrIds = this.attributesQuery.split(/[&,]/).filter(id => id.trim() && id !== '');
          } else if (Array.isArray(this.attributesQuery)) {
            attrIds = this.attributesQuery.filter(id => id);
          }
        }
        
        // Also add attribute IDs from filterQuery if not already in attributesQuery
        if (attrIds.length === 0 && filterParams.attribute_ids && Array.isArray(filterParams.attribute_ids)) {
          attrIds = filterParams.attribute_ids;
        } else if (filterParams.attribute_ids && Array.isArray(filterParams.attribute_ids)) {
          // Merge attribute IDs from both sources (avoid duplicates)
          const existingIds = new Set(attrIds.map(id => String(id)));
          filterParams.attribute_ids.forEach(id => {
            const idStr = String(id);
            if (!existingIds.has(idStr)) {
              attrIds.push(idStr);
            }
          });
        }
        
        // Add each attribute ID as array parameter
        if (attrIds.length > 0) {
          attrIds.forEach(id => {
            queryParts.push(`attribute_ids[]=${encodeURIComponent(id)}`);
          });
        }
        
        // Build and encode query string
        const queryString = queryParts.join("&");
        
        
        if (queryString) {
          const encodedQuery = btoa(encodeURIComponent(queryString));
          const currentQuery = this.$route.fullPath.split("?")[1] || "";
          
          
          // Only update if different to avoid unnecessary navigation
          if (currentQuery !== encodedQuery) {
            const newPath = `/products?${encodedQuery}`;
            const newUrl = `${window.location.origin}${newPath}`;
            
            // CRITICAL: Update URL synchronously using history API so FilterBar sees it immediately
            // This ensures FilterBar reads the correct query parameters when it initializes
            if (process.client && typeof window !== 'undefined' && window.history) {
              // Update URL synchronously without triggering navigation
              window.history.replaceState({ ...window.history.state }, '', newUrl);
              
              // Also update Vue Router's route object to keep it in sync
              // Use silent flag to avoid triggering watchers during restoration
              this.$router.replace(newPath, { silent: true }).catch(() => {
                // Ignore navigation errors - history API already updated the URL
              });
            } else {
              // Fallback to router replace if history API not available
              this.$router.replace(newPath, { silent: true }).catch(() => {
                // Ignore navigation errors
              });
            }
          }
        }
      }
      
      // Also call updateRouteQueryParams in nextTick as fallback to ensure everything is in sync
      this.$nextTick(() => {
        if (this.isMounted) {
          this.updateRouteQueryParams();
        }
      });

      console.log(
        "✅ Saved products page data displayed successfully in created()"
      );
      console.log("- Products count:", this.productsResult?.length || 0);
      console.log("- Current page:", this.currentPage);
      console.log("- Allow to load more:", this.allowToLoadMore);
      console.log("- Search input:", this.searchInpt);
      console.log("- Filter query:", this.filterQuery);

      this.markInitialProductsLoaded();

      return; // Exit early, no need for API calls
    } else {
      console.log(
        "❌ No saved products page data found in created(), proceeding with cache check"
      );
    }

    // Check for cached data if no saved data was found
    console.log("🔍 Checking for cached products data in created()...");

    // Cache logic for non-homepage navigation (homepage is handled above)
    // Only proceed with cache if not from homepage (already handled above)
    const currentFilters = this.getCurrentFilters();
    const cacheKey = safeProductListCache.generateCacheKey(
      currentFilters,
      this.sortBy || "date",
      null
    );

    console.log("🔍 Cache check details in created():");
    console.log("- Current filters:", currentFilters);
    console.log("- Sort by:", this.sortBy || "date");
    console.log("- Generated cache key:", cacheKey);

    // Check if we have cached data for the current page
    const hasCache = safeProductListCache.hasValidCache(
      cacheKey,
      1,
      currentFilters
    );
    console.log("Has cache in created()?", hasCache);

    // Let's also check what cache keys are available
    const availableCacheKeys = safeProductListCache.getCache();
    console.log("All available cache keys:", Object.keys(availableCacheKeys));

    // Check if there are any cache entries that might match
    if (Object.keys(availableCacheKeys).length > 0) {
      console.log("🔍 Available cache entries:");
      Object.keys(availableCacheKeys).forEach((key) => {
        const pages = Object.keys(availableCacheKeys[key]);
        console.log(`- ${key}: pages ${pages.join(", ")}`);
      });
    }

    // Only retrieve from cache if we have exact parameter match
    if (hasCache) {
      const targetCacheKey = cacheKey;
      const targetPage = 1;

      console.log(`✅ Found cached data, displaying immediately...`);
      console.log(`- Using cache key: ${targetCacheKey}`);
      console.log(`- Using page: ${targetPage}`);

      // Get the raw cached data first to inspect its structure
      const rawCachedData = safeProductListCache.getCompleteCachedData(
        targetCacheKey,
        targetPage,
        currentFilters
      );
      console.log("🔍 Raw cached data structure:", rawCachedData);

      // Display cached data immediately
      const result = safeProductListCache.smartCacheRetrieval(
        targetCacheKey,
        targetPage,
        currentFilters,
        {
          autoRestoreScroll: true,
          autoDisplayProducts: true,
          onDataRetrieved: (cachedData) => {
            console.log(
              "🎯 CALLBACK EXECUTED in created(): Displaying cached products:",
              cachedData.products?.length || "undefined"
            );
            console.log("🔍 Cached data structure:", cachedData);

            // The products are in cachedData.products.data (nested structure from API response)
            const products =
              cachedData.products?.data || cachedData.products || [];
            console.log("📦 Extracted products:", products.length);

            // Set the cached data immediately
            this.productsResult = products;
            this.currentPage = targetPage;
            this.allowToLoadMore = products && products.length >= 20;
            this.isLoading.results = false;
            this.isLoading.more = false;

            // Mark that we've displayed cached data to prevent override
            this.cachedDataDisplayed = true;
            this.markInitialProductsLoaded();

            console.log("✅ Cached data displayed successfully in created()");
          },
          onScrollRestored: (scrollPosition) => {
            console.log(
              "📜 Scroll position restored in created():",
              scrollPosition
            );
          },
          onComplete: (cachedData) => {
            console.log("🎉 Cache retrieval completed in created()");
          },
        }
      );

      if (result.success) {
        console.log(
          "🎉 Cache displayed successfully in created(), skipping API calls"
        );
        this.initialProductsLoadTriggered = true;
        this.isInitializingFilters = false;
        return; // Exit early, no need for API calls
      } else {
        console.log(
          "❌ Cache display failed in created(), will proceed with API calls"
        );
      }
    } else {
      console.log(
        "❌ No cache found in created(), proceeding with normal flow"
      );
    }

    await this.ensureInitialProductsLoaded();
  },
  watch: {
    async $route(to, from) {
      // react to route changes...
      if (
        this.isMounted &&
        to.path == from.path &&
        this.decodeQueryObj?.from == "list"
      ) {
        console.log(
          "🏠 Homepage navigation detected in route watcher - skipping cache"
        );

        if (Object.keys(this.decodeQueryObj).length) {
          console.log("🏠 Homepage route watcher: Processing query parameters");
          await this.getAndInjectQuery();

          // Clear cache when coming from homepage to ensure fresh data
          console.log(
            "🗑️ Clearing cache in route watcher for homepage navigation"
          );
          this.clearCacheOnFilterChange();

          // Make fresh API call after clearing cache
          console.log("🔄 Making fresh API call from route watcher");
          await this.getResults("search", 1, false, true); // Force skip cache
          // Filter stays closed by default - no auto-opening
        }
      }
    },

    // Simplified watchers - filter manager handles most logic via subscription
    // These watchers are kept for backward compatibility but are mostly bypassed
    filterQuery: {
      handler(newQuery, oldQuery) {
        // Skip if change came from filter manager (already handled via subscription)
        // Only process if change came from external source
        if (newQuery === oldQuery) return;
        
        // Skip during initialization or restoration
        if (this.isInitializingFilters || this.restorationManager.isRestoring.value) {
          return;
        }
        
        // Legacy fallback - should rarely trigger now
        console.log('⚠️ filterQuery watcher triggered (legacy fallback)');
      },
    },
    attributesQuery: {
      handler(newQuery, oldQuery) {
        // Skip if change came from filter manager
        if (newQuery === oldQuery) return;
        
        // Skip during initialization or restoration
        if (this.isInitializingFilters || this.restorationManager.isRestoring.value) {
          return;
        }
        
        // Legacy fallback - should rarely trigger now
        console.log('⚠️ attributesQuery watcher triggered (legacy fallback)');
      },
    },

    subAttrsSelected: {
      handler(newSubAttrsSelected, oldSubAttrsSelected) {
        let subAttrsSelectedItems = newSubAttrsSelected
          .filter((item) => !(item == undefined || item == null)) //clear subAttrsSelected from any null or undefined item
          .flat(); //[[{}, {}],[{}, {}]] --> [{},{},{},{}]

        let secondMainAttrsItems = subAttrsSelectedItems
          .map((subAttrsSelectedItem) => subAttrsSelectedItem?.attributes || [])
          .flat();
        this.secondMainAttrs = secondMainAttrsItems;

        // if you want to add parent to swcond main attrs
        // let newFormated=subAttrsSelectedItems.map(subAttrsSelectedItem=>
        //   subAttrsSelectedItem.attributes.map(attribute=>({
        //       ...attribute,
        //       parent:{
        //         id:subAttrsSelectedItem?.id,
        //         name_ar:subAttrsSelectedItem?.name_ar,
        //         name_en:subAttrsSelectedItem?.name_en
        //       }
        //     })
        //   )
        // ).flat()
        // this.secondMainAttrs=newFormated
      },
      deep: true,
      immediate: false,
    },
    secondMainAttrs: {
      handler(newArray, oldArray) {
        //detect id of element that removed from array
        const oldIds = oldArray.map((item) => item.id);
        const newIds = newArray.map((item) => item.id);
        const removedIds = oldIds.filter((id) => !newIds.includes(id));

        let clone = { ...this.secondSubAttrsSelected };
        removedIds.forEach((removedId) => {
          delete clone[removedId];
        });
        this.secondSubAttrsSelected = { ...clone };
      },
      deep: true,
      immediate: false,
    },

    categoryId: {
      handler(newArray, oldArray) {
        this.getExclusiveOffers();
        this.getCategorySeoContent();
      },
    },
  },
  computed: {
    ...mapState(useGlobalStore, ["getStoredUserGeoLocation"]),
    ...mapState(useGlobalStore, ["savedProductspageData"]),
    currentCategoryMetaTitle() {
      const locale = this.$i18n.locale || "ar";
      const metaTitle =
        locale === "ar"
          ? this.categorySeoContent.meta_title_ar
          : this.categorySeoContent.meta_title_en;

      console.log("🔍 currentCategoryMetaTitle computed:", {
        locale,
        meta_title_ar: this.categorySeoContent.meta_title_ar,
        meta_title_en: this.categorySeoContent.meta_title_en,
        result: metaTitle,
      });

      return metaTitle;
    },
    currentCategoryMetaDescription() {
      const locale = this.$i18n.locale || "ar";
      const metaDescription =
        locale === "ar"
          ? this.categorySeoContent.meta_description_ar
          : this.categorySeoContent.meta_description_en;

      console.log("🔍 currentCategoryMetaDescription computed:", {
        locale,
        meta_description_ar: this.categorySeoContent.meta_description_ar,
        meta_description_en: this.categorySeoContent.meta_description_en,
        result: metaDescription,
      });

      return metaDescription;
    },
    currentCategoryImageAlt() {
      const imageAlt = this.categorySeoContent.image_alt_text;

      return imageAlt;
    },
    currentCategoryKeywords() {
      const keywords = this.categorySeoContent.meta_keywords;

      console.log(
        "🔍 currentCategoryKeywords computed:",
        this.categorySeoContent
      );

      return keywords;
    },
    currentCategorySeoContent() {
      const locale = this.$i18n.locale || "ar";

      console.log("seo content", this.categorySeoContent);

      return locale === "ar"
        ? this.categorySeoContent.content_ar
        : this.categorySeoContent.content_en;
    },
    displayedSeoContent() {
      if (!this.currentCategorySeoContent) return "";
      if (this.isSeoContentExpanded) {
        return this.currentCategorySeoContent;
      }
      if (this.currentCategorySeoContent.length <= 300) {
        return this.currentCategorySeoContent;
      }
      return this.currentCategorySeoContent.substring(0, 300) + "...";
    },
    shouldShowReadMore() {
      if (!this.currentCategorySeoContent) return false;
      return this.currentCategorySeoContent.length > 300;
    },
    breadcrumbLinks() {
      return [
        { text: this.$t("Breadcrumb.home"), path: "/" },
        { text: this.$t("Breadcrumb.products"), path: "/products" },
      ];
    },
    showInitialPageLoading() {
      return !this.hasInitialProductsLoaded && this.isLoading.results;
    },

    decodeQueryObj() {
      // Only run on client side to avoid SSR issues
      if (process.server) {
        return {};
      }

      const route = useRoute();
      try {
        const queryString = route.fullPath.split("?")[1];

        // If no query string, return empty object
        if (!queryString) {
          return {};
        }

        // Try to decode as base64 first, if that fails, treat as regular query string
        let decodeQuery;
        try {
          decodeQuery = decodeURIComponent(atob(queryString));
        } catch (base64Error) {
          // If base64 decode fails, use the query string as-is
          console.log("Using regular query string (not base64 encoded)");
          decodeQuery = queryString;
        }

        // Safely parse the query string
        let queryObj = {};
        try {
          queryObj = parseQueryString(decodeQuery);
        } catch (parseError) {
          console.error("Error parsing query string:", parseError);
          // Return empty object if parsing fails
        }

        return queryObj;
      } catch (error) {
        console.error("Error decoding query object:", error);
        return {};
      }
    },
    encodedRouteFilterQuery() {
      if (process.server) {
        return "";
      }

      const route = useRoute();
      const queryString = route.fullPath.split("?")[1] || "";

      if (!queryString) {
        return "";
      }
      localStorage.setItem("encodedFilterQuery", queryString);
      localStorage.setItem("searchTerm", this.searchInpt || "");
      try {
        // If already base64 encoded, atob will succeed
        atob(queryString);
        return queryString;
      } catch (decodeError) {
        try {
          const sanitized = queryString.replace(/^\?/, "");
          return btoa(encodeURIComponent(sanitized));
        } catch (encodeError) {
          console.error("Failed to encode route filter query:", encodeError);
          return "";
        }
      }
    },
  },
  mounted() {
    if (process.client) {
      // Initialize product cache system
      this.safeProductListCache = safeProductListCache;
      this.isMounted = true;

      // Subscribe to filter manager changes
      this.filterManager.subscribeToChanges((filterState, source) => {
        this.onFilterStateChanged(filterState, source);
      });

      // Mark this as navigation to preserve cache
      this.markAsNavigation();

      // Set up navigation detection
      this.setupNavigationDetection();

      // Set up scroll position saving when products are loaded
      this.$nextTick(() => {
        if (this.productsResult && this.productsResult.length > 0) {
          const currentFilters = this.getCurrentFilters();
          const cacheKey = safeProductListCache.generateCacheKey(
            currentFilters,
            this.sortBy || "date",
            null
          );
          const currentPage = this.currentPage || 1;
          safeProductListCache.setupScrollSaving(cacheKey, currentPage);
        }
      });

      // Restore filter state from URL via manager (only if not restoring from product context)
      // If we have product context, filters will be restored in tryRestoreProductPosition
      const hasProductContext = this.restorationManager.shouldAttemptRestoration('products');
      if (!hasProductContext) {
        this.filterManager.restoreFromUrl();
        // Mark as initialized if no restoration needed
        setTimeout(() => {
          this.isInitializingFilters = false;
          this.filterManager.markInitialized();
        }, 500);
      }
      
      // Check if we have cached data
      if (
        !this.cachedDataDisplayed &&
        !this.initialProductsLoadTriggered &&
        (this.$route.query.search ||
          this.$route.query.category ||
          this.$route.query.sort)
      ) {
        this.getResults();
      } else if (this.cachedDataDisplayed) {
        console.log(
          "✅ Skipping getResults in mounted() since cached data was already displayed"
        );
      }
      
      // Restore scroll position when returning from product page
      // Wait for filters to be initialized before attempting restoration
      this.$nextTick(() => {
        // Wait a bit more to ensure filters are fully restored
        setTimeout(() => {
          if (!this.isInitializingFilters) {
            this.restoreScrollPosition();
          } else {
            // If still initializing, wait a bit more
            setTimeout(() => {
              this.restoreScrollPosition();
            }, 300);
          }
        }, 200);
      });
    }
  },
  beforeUnmount() {
    // Remove navigation detection
    this.removeNavigationDetection();

    // Remove scroll saving
    safeProductListCache.removeScrollSaving();
  },
  unmounted() {
    this.isUnmounted = true;
    window.removeEventListener("scroll", this.fadeButtonOnMobile);
  },
  methods: {
    markInitialProductsLoaded() {
      if (!this.hasInitialProductsLoaded) {
        this.hasInitialProductsLoaded = true;
      }
    },
    async ensureInitialProductsLoaded() {
      if (this.initialProductsLoadTriggered || this.cachedDataDisplayed) {
        this.isInitializingFilters = false;
        return;
      }

      try {
        await this.getResults();
      } finally {
        this.isInitializingFilters = false;
      }
    },
    clearSearch() {
      this.searchInpt = "";
      this.clearCacheOnFilterChange();
      this.getResults("", 1, false, true); // Force skip cache
    },
    handleSearchSubmit() {
      // Clear product positions when search changes
      const oldCacheKey = safeProductListCache.generateCacheKey(
        this.getCurrentFilters(),
        this.sortBy || "date",
        null
      );
      safeProductListCache.clearProductPositions(oldCacheKey);
      console.log("🗑️ Cleared product positions due to search change");
      
      // Clear cache and reset flags to ensure fresh API call
      this.currentPage = 1;
      this.allowToLoadMore = true;
      this.clearSavedProductsData();
      this.clearCacheOnFilterChange();
      this.cachedDataDisplayed = false;
      
      // Update route query parameters to preserve filters
      this.updateRouteQueryParams();
      
      // Force skip cache to always make a fresh request
      this.getResults("", 1, false, true);
    },
    fadeButtonOnMobile() {
      const myButton = document.getElementById("myButton");
      let scrollTimeout;
      // Hide the button when scrolling
      myButton.classList.add("opacity-20");

      // Clear the previous timeout to reset the delay
      clearTimeout(scrollTimeout);

      // Set a timeout to show the button again after scrolling stops (200ms)
      scrollTimeout = setTimeout(() => {
        myButton.classList.remove("opacity-20");
      }, 200); // Adjust delay time as needed
    },

    /**
     * Normalize filter query string into a comparable object
     * Parses query string and extracts all filter parameters
     */
    normalizeFilterQuery(queryString) {
      if (!queryString) return {};
      
      const params = {};
      const pairs = queryString.split('&');
      
      for (const pair of pairs) {
        const [key, value] = pair.split('=');
        if (!key || !value) continue;
        
        // Handle array parameters like attribute_ids[]
        if (key.includes('[]')) {
          const arrayKey = key.replace('[]', '');
          if (!params[arrayKey]) {
            params[arrayKey] = [];
          }
          params[arrayKey].push(decodeURIComponent(value));
        } else {
          params[key] = decodeURIComponent(value);
        }
      }
      
      // Sort array values for consistent comparison
      Object.keys(params).forEach(key => {
        if (Array.isArray(params[key])) {
          params[key].sort();
        }
      });
      
      return params;
    },

    /**
     * Compare two filter objects to determine if they represent the same filters
     * Returns true if filters are equivalent (same values, ignoring order)
     */
    compareFilterObjects(obj1, obj2) {
      const keys1 = Object.keys(obj1).sort();
      const keys2 = Object.keys(obj2).sort();
      
      if (keys1.length !== keys2.length) {
        return false;
      }
      
      for (const key of keys1) {
        if (!keys2.includes(key)) {
          return false;
        }
        
        const val1 = obj1[key];
        const val2 = obj2[key];
        
        if (Array.isArray(val1) && Array.isArray(val2)) {
          if (val1.length !== val2.length) {
            return false;
          }
          const sorted1 = [...val1].sort();
          const sorted2 = [...val2].sort();
          if (JSON.stringify(sorted1) !== JSON.stringify(sorted2)) {
            return false;
          }
        } else if (val1 !== val2) {
          return false;
        }
      }
      
      return true;
    },

    /**
     * Unified filter state change handler
     * Replaces handleQuery, handleAttributes, handleCategoryId
     */
    onFilterStateChanged(filterState, source) {
      console.log("🔄 Filter state changed:", { source, filterState });
      
      // Skip if restoration is in progress (unless explicitly allowed)
      if (this.restorationManager.isRestoring.value && source !== 'restoration') {
        console.log('⏸️ Filter change skipped - restoration in progress');
        return;
      }
      
      // Update local state for backward compatibility
      this.filterQuery = filterState.filterQuery || "";
      this.attributesQuery = filterState.attributesQuery || "";
      this.categoryId = filterState.category_id || filterState.main_category_id || "0";
      this.sortBy = filterState.sort_by || "date";
      this.sortType = filterState.sort_type || "desc";
      
      // Mark user interaction
      if (source !== 'url' && source !== 'restoration') {
        this.hasUserInteractedWithFilters = true;
      }
      
      // Clear cache and reset pagination
      this.currentPage = 1;
      this.allowToLoadMore = true;
      this.clearSavedProductsData();
      this.clearCacheOnFilterChange();
      
      // Load results with new filters
      this.getResults();
    },

    // Legacy handler for backward compatibility during transition
    handleQuery(val) {
      // Parse query string and update via filter manager
      if (!val || val.trim() === '') {
        // Reset filters
        this.filterManager.resetFilters();
        return;
      }
      
      // Parse query string to filter state
      const queryParams = new URLSearchParams(val);
      const updates = {};
      
      // Extract all parameters
      for (const [key, value] of queryParams.entries()) {
        if (key === 'attribute_ids[]') {
          if (!updates.attribute_ids) updates.attribute_ids = [];
          updates.attribute_ids.push(value);
        } else if (key === 'category_id') {
          updates.category_id = value;
        } else if (key === 'min_price') {
          updates.min_price = parseInt(value, 10);
        } else if (key === 'max_price') {
          updates.max_price = parseInt(value, 10);
        } else if (key === 'min_space') {
          updates.min_space = parseInt(value, 10);
        } else if (key === 'max_space') {
          updates.max_space = parseInt(value, 10);
        } else if (key === 'is_negotiable') {
          updates.is_negotiable = value === '1' || value === 'true';
        } else if (key === 'sort_by') {
          updates.sort_by = value;
          updates.userHasSelectedSorting = true;
        } else if (key === 'sort_type') {
          updates.sort_type = value;
        } else if (key === 'sort_by_force') {
          updates.sort_by_force = value;
        } else if (key === 'product_type') {
          updates.product_type = value;
        } else if (key === 'rating') {
          updates.rating = value;
        } else if (key === 'governorate_id') {
          updates.governorate_id = value;
        } else if (key === 'area_id') {
          updates.area_id = value;
        } else if (key === 'store_id') {
          updates.store_id = value;
        }
      }
      
      // Update via filter manager
      this.filterManager.updateFilterState('legacy-handleQuery', updates);
    },

    _processHandleQuery(val) {
      // Set flag to prevent watcher from clearing cache
      this._handlingQueryChange = true;
      
      // Check if query actually changed (normalize both for comparison)
      const normalizedNewQuery = val || "";
      const normalizedOldQuery = this.filterQuery || "";
      
      // Parse filter queries into comparable objects
      const newFilterObj = this.normalizeFilterQuery(normalizedNewQuery);
      const oldFilterObj = this.normalizeFilterQuery(normalizedOldQuery);
      
      // Check if filters are actually the same (just normalized)
      const filtersAreEquivalent = this.compareFilterObjects(newFilterObj, oldFilterObj);
      
      // Extract category IDs for logging and comparison
      const newCategoryIdFromQuery = newFilterObj.category_id || "";
      const oldCategoryIdFromQuery = oldFilterObj.category_id || "";
      
      // If filters are equivalent (same values, just normalized), check if we should skip
      if (filtersAreEquivalent && normalizedOldQuery !== "") {
        // Priority 1: Skip if we're currently restoring a product position
        if (this.restorationManager.isRestoring.value) {
          console.log('⏸️ Skipping handleQuery during product position restoration (filters equivalent)');
          this._handlingQueryChange = false;
          return;
        }
        
        // Priority 4: Check if we have product context (restoration just completed)
        // If context exists and filters match, it's FilterBar normalization after restoration
        const productContext = this.getStoredProductNavigationContext();
        if (productContext && productContext.portalPage === 'products' && !this.hasUserInteractedWithFilters) {
          // Extract category from context cache key to verify it matches
          const contextCategoryId = productContext.cacheKey.match(/category:(\d+)/)?.[1] || "";
          
          // If category matches context OR context doesn't specify category, skip normalization
          if (contextCategoryId === newCategoryIdFromQuery || contextCategoryId === "") {
            console.log('⏸️ FilterBar normalization detected (same filters, context exists), skipping update');
            console.log('⏸️ Context:', { contextCategoryId, newCategoryIdFromQuery, hasContext: !!productContext });
            this._handlingQueryChange = false;
            return; // Don't update filterQuery to avoid triggering watcher
          }
        }
        
        // Priority 5: If no user interaction and filters equivalent, it's likely normalization
        // Only skip if we're within restoration period (check timestamp if available)
        if (!this.hasUserInteractedWithFilters) {
          // Check if we recently completed restoration (within last 10 seconds - extended from 5)
          // This is a safety net in case context was cleared but restoration just finished
          const context = this.getStoredProductNavigationContext();
          if (context && context.timestamp) {
            const timeSinceRestoration = Date.now() - context.timestamp;
            if (timeSinceRestoration < 10000) { // Within 10 seconds
              console.log('⏸️ FilterBar normalization detected (recent restoration, filters equivalent), skipping update');
              this._handlingQueryChange = false;
              return; // Don't update filterQuery to avoid triggering watcher
            }
          }
        }
      }
      
      // Skip if we're currently restoring a product position (even if filters changed)
      if (this.restorationManager.isRestoring.value) {
        console.log('⏸️ Skipping handleQuery during product position restoration');
        this._handlingQueryChange = false;
        return;
      }
      
      this.hasUserInteractedWithFilters = true;

      // Category IDs already extracted above, reuse them
      const currentCategoryId = this.categoryId || "0";
      const newCategoryId = newCategoryIdFromQuery;

      console.log("📊 Category change detected:", {
        old: currentCategoryId,
        new: newCategoryId,
      });

      // Clear cached data when filters change
      this.clearCacheOnFilterChange();
      
      // Clear product positions when filters change
      const oldCacheKey = safeProductListCache.generateCacheKey(
        this.getCurrentFilters(),
        this.sortBy || "date",
        null
      );
      safeProductListCache.clearProductPositions(oldCacheKey);
      console.log("🗑️ Cleared product positions due to filter change");

      // Reset pagination
      this.currentPage = 1;
      this.allowToLoadMore = true;

      // Update filterQuery - handle empty query (reset) and category_id=0 for "all categories"
      if (!val || val.trim() === '' || val.includes("category_id=0")) {
        this.filterQuery = ``;
        this.categoryId = "0"; // Set to "0" for "all categories" when reset
      } else {
        this.filterQuery = val;
        this.categoryId = newCategoryId || "0";
      }

      // Update route query parameters to preserve filters
      this.updateRouteQueryParams();

      // Call getResults() since we're handling the change here
      // Use nextTick to ensure filterQuery watcher sees the updated value after flag is cleared
      this.$nextTick(() => {
        this.getResults();
        // Clear flag after a short delay to allow watcher to see it was handled
        setTimeout(() => {
          this._handlingQueryChange = false;
        }, 100);
      });

      console.log("✅ Filter query updated successfully");
    },
    // Legacy handler for backward compatibility
    handleAttributes(val) {
      // Parse attributes string (comma-separated IDs) and update via filter manager
      const attributeIds = val ? val.split(',').filter(id => id.trim()) : [];
      
      this.filterManager.updateFilterState('legacy-handleAttributes', {
        attribute_ids: attributeIds,
      });
    },
    
    // Legacy handler for backward compatibility
    handleCategoryId(val) {
      const categoryId = val || "0";
      
      this.filterManager.updateFilterState('legacy-handleCategoryId', {
        category_id: categoryId === "0" ? null : categoryId,
      });
    },
    openFilter() {
      this.isPopup = true;
    },
    closeFilter() {
      this.isPopup = false;
    },
    getExclusiveOffers() {
      // Skip on server-side
      if (process.server) {
        return Promise.resolve();
      }

      return new Promise((resolve, reject) => {
        this.isLoadingExclusiveOffers = true;
        this.makeRequest({
          url: `${this.APIREQUESTS.user.getExclusiveOffers.url}${this.categoryId ? `?category_id=${this.categoryId}` : "?category_id=0"}`,
          method: this.APIREQUESTS.user.getExclusiveOffers.method,
        })
          .then((res) => {
            this.isLoadingExclusiveOffers = false;
            if (res?.status) {
              resolve();
              let exclusiveOffers = [];
              if (Array.isArray(res?.data)) {
                exclusiveOffers = res.data;
              } else {
                exclusiveOffers = [];
              }
              this.exclusiveOffersData = exclusiveOffers;
            } else {
              this.notify({
                type: "error",
                msg: res?.message || "Failed to get the offers data.",
              });
              reject("Failure: The promise has failed!", error);
            }
          })
          .catch((err) => {
            console.error(err);
            reject("Failure: The promise has failed!", err);
            if (!err || err.code !== "axios_cancelled") {
              this.notify({
                type: "error",
                msg: err?.data?.message || "Failed to get the offers data.",
              });
            }
            this.isLoadingExclusiveOffers = false;
          });
      });
    },
    getCategorySeoContent() {
      // Skip on server-side
      if (process.server) {
        return Promise.resolve();
      }

      // Skip if no category ID or if it's "all categories"
      if (!this.categoryId || this.categoryId === "0") {
        this.categorySeoContent = {
          content_ar: "",
          content_en: "",
          meta_title_ar: "",
          meta_title_en: "",
          meta_description_ar: "",
          meta_description_en: "",
          image_alt_text: "",
          meta_keywords: "",
        };
        return Promise.resolve();
      }

      return new Promise((resolve, reject) => {
        this.makeRequest({
          url: `${this.APIREQUESTS.user.getCategorySeo.url}?category_id=${this.categoryId}`,
          method: this.APIREQUESTS.user.getCategorySeo.method,
        })
          .then((res) => {
            if (res && res.status && res.data) {
              this.categorySeoContent = {
                content_ar: res.data.content_ar || "",
                content_en: res.data.content_en || "",
                meta_title_ar: res.data.meta_title_ar || "",
                meta_title_en: res.data.meta_title_en || "",
                meta_description_ar: res.data.meta_description_ar || "",
                meta_description_en: res.data.meta_description_en || "",
                image_alt_text: res.data.image_alt_text || "",
                meta_keywords: res.data.meta_keywords || "",
              };
              console.log("✅ Fetched category SEO content:", res.data);
            } else {
              this.categorySeoContent = {
                content_ar: "",
                content_en: "",
                meta_title_ar: "",
                meta_title_en: "",
                meta_description_ar: "",
                meta_description_en: "",
                image_alt_text: "",
                meta_keywords: "",
              };
            }
            resolve();
          })
          .catch((err) => {
            console.error("Error fetching category SEO content:", err);
            this.categorySeoContent = {
              content_ar: "",
              content_en: "",
              meta_title_ar: "",
              meta_title_en: "",
              meta_description_ar: "",
              meta_description_en: "",
              image_alt_text: "",
              meta_keywords: "",
            };
            reject(err);
          });
      });
    },
    async getResults(
      prevProductsResult = [],
      currentPage = 1,
      isLoadingMore = false,
      forceSkipCache = false
    ) {
      this.initialProductsLoadTriggered = true;
      // Skip API calls if cached data was already displayed
      if (this.cachedDataDisplayed && !isLoadingMore && !forceSkipCache) {
        console.log(
          "✅ Skipping API call since cached data was already displayed"
        );
        return;
      }

      console.log("🔄 getResults called:", {
        prevProductsResult: !!prevProductsResult,
        currentPage,
        isLoadingMore,
        forceSkipCache,
      });

      // Reset pagination when not loading more (i.e., when searching or applying filters)
      if (!isLoadingMore) {
        this.currentPage = 1;
        this.allowToLoadMore = true;
      }

      // Detect if sorting has changed
      const currentSortBy = this.sortBy || "date";
      const isSortingChanged = this.previousSortBy !== null && this.previousSortBy !== currentSortBy;
      
      // Clear product positions when sorting changes
      if (isSortingChanged && !isLoadingMore) {
        const oldCacheKey = safeProductListCache.generateCacheKey(
          this.getCurrentFilters(),
          this.previousSortBy || "date",
          null
        );
        // Clear product positions for the old cache key
        safeProductListCache.clearProductPositions(oldCacheKey);
        console.log("🗑️ Cleared product positions due to sorting change");
      }
      
      // Update previous sortBy for next comparison
      if (!isLoadingMore) {
        this.previousSortBy = currentSortBy;
      }

      // Generate cache key based on current filters and search
      const currentFilters = this.getCurrentFilters();
      const cacheKey = safeProductListCache.generateCacheKey(
        currentFilters,
        currentSortBy,
        null
      );

      // Check cache first (only for non-load-more requests and when not forcing skip)
      if (
        !isLoadingMore &&
        safeProductListCache.isCacheAvailable() &&
        !forceSkipCache
      ) {
        console.log("🔍 Cache check started...");
        console.log("Cache key:", cacheKey);
        console.log("Current page:", currentPage);
        console.log("Current filters:", currentFilters);

        // Check if we have valid cache first
        const hasCache = safeProductListCache.hasValidCache(
          cacheKey,
          currentPage,
          currentFilters
        );
        console.log("Has valid cache?", hasCache);

        if (hasCache) {
          console.log(
            "✅ Exact cache match found, attempting smart retrieval..."
          );

          // Check if we have a saved product position to restore
          const savedProductContext = this.getStoredProductNavigationContext();
          const shouldRestoreToProduct = savedProductContext && 
                                         savedProductContext.portalPage === 'products' &&
                                         savedProductContext.cacheKey === cacheKey;

          const result = safeProductListCache.smartCacheRetrieval(
            cacheKey,
            currentPage,
            currentFilters,
            {
              // Disable general scroll restoration if we're restoring to a product, or if sorting changed
              autoRestoreScroll: !isSortingChanged && !shouldRestoreToProduct,
              autoDisplayProducts: true,
              onDataRetrieved: (cachedData) => {
                console.log(
                  "🎯 CALLBACK EXECUTED: Automatically displaying cached products:",
                  cachedData.products.length
                );

                // Use cached data
                this.productsResult = cachedData.products || [];
                this.allowToLoadMore =
                  cachedData.products && cachedData.products.length >= 20;
                this.markInitialProductsLoaded();
              },
              onScrollRestored: (scrollPosition) => {
                console.log(
                  "📜 Scroll position restored automatically:",
                  scrollPosition
                );
              },
              onComplete: (cachedData) => {
                console.log("🎉 Cache retrieval completed successfully");
                // Update loading states
                this.isLoading.results = false;
                this.isLoading.more = false;

                // Store data in global state
                this.global.updateProductspageData({ ...this.$data });
                
                // Try to restore product position after cache restoration
                this.tryRestoreProductPosition();
              },
            }
          );

          console.log("Smart cache retrieval result:", result);

          if (result.success) {
            console.log("🎉 Smart cache retrieval successful:", result.message);
            return; // Exit early, no API call needed
          } else {
            console.log("❌ Smart cache retrieval failed:", result.reason);
          }
        } else {
          console.log("❌ No valid cache found");

          // Let's see what cache info we have
          const cacheInfo = safeProductListCache.getCacheInfo(
            cacheKey,
            currentPage,
            currentFilters
          );
          console.log("Cache info:", cacheInfo);

          // Check what's in the cache
          const allCache = safeProductListCache.getCache();
          console.log("All cache keys:", Object.keys(allCache));
          if (allCache[cacheKey]) {
            console.log(
              "Cache key exists, pages:",
              Object.keys(allCache[cacheKey])
            );
          }
        }
      } else {
        console.log("🔍 Cache check skipped:", {
          isLoadingMore,
          isCacheAvailable: safeProductListCache.isCacheAvailable(),
          forceSkipCache,
          reason: forceSkipCache
            ? "Force skip cache mode (homepage navigation)"
            : "Normal skip conditions",
        });
      }

      isLoadingMore
        ? (this.isLoading.results = false)
        : (this.isLoading.results = true);

      try {
        console.log("🌐 Making fresh API call for products:", {
          url: "products",
          search: this.searchInpt,
          categoryId: this.categoryId,
          filterQuery: this.filterQuery,
          forceSkipCache,
        });

        // Add default sorting if filterQuery doesn't already include sort parameters
        let queryString = this.filterQuery;
        if (
          !queryString.includes("sort_by") &&
          !queryString.includes("sort_type")
        ) {
          queryString = `${queryString}${queryString ? "&" : ""}`;
        }

        const res = await this.makeRequest({
          url: `${this.APIREQUESTS.user.products.url}?with_ads=1&page=${this.currentPage}&${this.searchInpt ? `search=${this.searchInpt}&` : ""}${this.attributesQuery ? `attribute_ids[]=${this.attributesQuery}&` : ""}${queryString}`,
          method: this.APIREQUESTS.user.products.method,
          includeUID: true,
        });
        //check response
        if (!res.status) {
          //not ok
          this.notify({
            type: "error",
            msg: res?.message || this.$t("general.failed"),
          });
        } else {
          //ok
          this.productsResult = res?.data || [];

          // Only append to previous results if loading more
          if (isLoadingMore) {
            this.productsResult = [
              ...prevProductsResult,
              ...this.productsResult,
            ];
          }

          if (this.productsResult.length < 20) {
            this.allowToLoadMore = false;
          }

          this.markInitialProductsLoaded();

          // After pagination completes, try to restore product position if needed
          const pendingRestore = this.restorationManager.restorationState.pendingProductRestore;
          if (pendingRestore) {
            const { productId, page } = pendingRestore;
            
            this.$nextTick(() => {
              setTimeout(() => {
                // Check if we've reached the target page
                if (this.currentPage >= page) {
                  console.log(`✅ Reached target page ${page}, restoring product position...`);
                  this.restoreProductPositionAfterPagination(productId, page);
                  this.restorationManager.clearPendingProductRestore();
                } else {
                  // Continue loading pages until we reach the target
                  console.log(`⏳ Still loading pages... current: ${this.currentPage}, target: ${page}`);
                  this.loadPagesUntilTarget(page, productId);
                }
              }, 400); // Wait for DOM to update with new products
            });
          }

          // Save to cache with scroll position
          safeProductListCache.saveToCache(
            cacheKey,
            res,
            currentPage,
            currentFilters,
            safeProductListCache.getCurrentScrollPosition()
          );
        }
      } catch (err) {
        this.notify({ type: "error", msg: err?.data?.message });
      } finally {
        this.isLoading.results = false;
        this.isLoading.more = false;
        if (!this.hasInitialProductsLoaded) {
          this.markInitialProductsLoaded();
        }
      }

      //store all data in pinia at updated
      this.global.updateProductspageData({ ...this.$data });

      // Also save to global store for navigation persistence
      this.global.updateProductspageData({
        productsResult: this.productsResult,
        currentPage: this.currentPage,
        allowToLoadMore: this.allowToLoadMore,
        searchInpt: this.searchInpt,
        filterQuery: this.filterQuery,
        attributesQuery: this.attributesQuery,
        categoryId: this.categoryId,
        isLoading: this.isLoading,
      });
    },
    async getAllCategories() {
      this.isLoading.categories = true;
      try {
        const res = await this.makeRequest({
          url: this.APIREQUESTS.user.categories.url,
          method: this.APIREQUESTS.user.categories.method,
        });

        //check response
        if (!res.status) {
          //not ok
          // this.notify({type: "error",msg: res?.message||this.$t('general.failed')})
        } else {
          //ok

          // Store data in global state if the request was successful
          const categoriesData = res?.data || [];
          const handeledData = categoriesData?.map((item) => {
            item.isOpen = false;
            item.children.isActive = false;

            return item;
          });
          this.global.setFilterCategories(handeledData);
        }
      } catch (err) {
        this.notify({ type: "error", msg: err?.data?.message });
      } finally {
        this.isLoading.categories = false;
      }
    },

    loadMoreProducts() {
      this.isLoading.more = true;

      this.currentPage++;
      let prevProductsResult = [...this.productsResult];
      this.getResults(prevProductsResult, this.currentPage, true);
    },

    /**
     * Get current filter state for caching purposes
     * Uses filter manager state as single source of truth
     */
    getCurrentFilters() {
      // Get current state from filter manager
      const filterState = this.filterManager.getFilterState();
      
      // Build filters object in format expected by cache manager
      const filters = {
        search: filterState.search || this.searchInpt || "",
        category: filterState.category_id || filterState.main_category_id || "",
        filterQuery: filterState.filterQuery || "",
        attributes: filterState.attributesQuery || (filterState.attribute_ids || []).join(','),
        store: filterState.store_id || "",
        governorate: filterState.governorate_id || "",
        area: filterState.area_id || "",
        priceMin: filterState.min_price || "",
        priceMax: filterState.max_price || "",
        isNegotiable: filterState.is_negotiable !== undefined ? filterState.is_negotiable : true,
        spaceMin: filterState.min_space || "",
        spaceMax: filterState.max_space || "",
        date: filterState.sort_type || "desc",
        sortBy: filterState.sort_by || "date",
        sortType: filterState.sort_type || "desc",
      };
      
      // Use cache manager's normalization to ensure consistent format
      if (this.safeProductListCache && typeof this.safeProductListCache.normalizeFilters === 'function') {
        const normalized = this.safeProductListCache.normalizeFilters(filters);
        return normalized;
      }
      
      // Fallback: manual normalization if cache manager not available
      return this.normalizeFiltersLocal(filters);
    },
    
    /**
     * Local fallback normalization method
     * Parses filterQuery string to extract all parameters
     */
    normalizeFiltersLocal(filters) {
      const normalized = {};
      
      // Parse filterQuery string if present
      if (filters.filterQuery && typeof filters.filterQuery === 'string' && filters.filterQuery.trim() !== '') {
        try {
          const queryParams = new URLSearchParams(filters.filterQuery);
          
          for (const [key, value] of queryParams.entries()) {
            // Handle array parameters like attribute_ids[]
            if (key.includes('[]') || key.includes('[')) {
              const arrayKey = key.replace(/\[\d*\]/g, '').replace('[]', '');
              if (!normalized[arrayKey]) {
                normalized[arrayKey] = [];
              }
              const parsedValue = decodeURIComponent(value);
              if (!normalized[arrayKey].includes(parsedValue)) {
                normalized[arrayKey].push(parsedValue);
              }
            } else {
              normalized[key] = decodeURIComponent(value);
            }
          }
          
          // Sort array values
          Object.keys(normalized).forEach(key => {
            if (Array.isArray(normalized[key])) {
              normalized[key].sort();
            }
          });
        } catch (error) {
          console.warn('Failed to parse filterQuery in normalizeFiltersLocal:', error);
        }
      }
      
      // Merge individual properties (take precedence)
      const propertyMap = {
        search: 'search',
        category: 'category_id',
        attributes: 'attribute_ids',
        store: 'store_id',
        governorate: 'governorate_id',
        area: 'area_id',
        priceMin: 'min_price',
        priceMax: 'max_price',
        spaceMin: 'min_space',
        spaceMax: 'max_space',
        isNegotiable: 'is_negotiable',
        date: 'sort_type',
        sortBy: 'sort_by',
        sortType: 'sort_type',
      };
      
      Object.keys(filters).forEach(key => {
        if (key === 'filterQuery') return;
        
        const value = filters[key];
        if (value === null || value === undefined || (value === '' && typeof value !== 'boolean')) {
          return;
        }
        
        const normalizedKey = propertyMap[key] || key;
        
        if (normalizedKey === 'attribute_ids') {
          let attrIds = [];
          if (typeof value === 'string' && value.trim() !== '') {
            attrIds = value.split(/[&,]/).map(id => id.trim()).filter(id => id !== '');
          } else if (Array.isArray(value)) {
            attrIds = value.map(id => String(id)).filter(id => id !== '');
          }
          
          if (attrIds.length > 0) {
            if (!normalized.attribute_ids) {
              normalized.attribute_ids = [];
            }
            const combined = [...new Set([...normalized.attribute_ids, ...attrIds])];
            normalized.attribute_ids = combined.sort();
          }
        } else if (normalizedKey === 'category_id') {
          const categoryValue = String(value).trim();
          if (categoryValue && categoryValue !== '0') {
            normalized.category_id = categoryValue;
          }
        } else if (normalizedKey === 'is_negotiable') {
          normalized.is_negotiable = value === true || value === 'true' || value === 1 || value === '1';
        } else {
          const stringValue = String(value).trim();
          if (stringValue !== '') {
            normalized[normalizedKey] = stringValue;
          }
        }
      });
      
      // Clean up empty values
      Object.keys(normalized).forEach(key => {
        if (normalized[key] === null || normalized[key] === undefined) {
          delete normalized[key];
        } else if (Array.isArray(normalized[key]) && normalized[key].length === 0) {
          delete normalized[key];
        } else if (typeof normalized[key] === 'string' && normalized[key].trim() === '') {
          delete normalized[key];
        }
      });
      
      // Sort keys
      const sortedKeys = Object.keys(normalized).sort();
      const result = {};
      sortedKeys.forEach(key => {
        result[key] = normalized[key];
      });
      
      return result;
    },

    /**
     * Clear cache for specific filters or all cache
     */
    clearProductCache(cacheKey = null) {
      if (cacheKey) {
        safeProductListCache.clearCacheByKey(cacheKey);
      } else {
        safeProductListCache.clearAllCache();
      }
    },

    /**
     * Get cache statistics for debugging
     */
    getCacheStats() {
      return safeProductListCache.getCacheStats();
    },

    /**
     * Clear cache when filters change, especially category changes
     */
    clearCacheOnFilterChange() {
      console.log("🗑️ Clearing cache due to filter/category change");
      console.log("- Current filters:", this.getCurrentFilters());

      // Clear all cache when any filter changes to prevent cross-contamination
      this.clearProductCache();

      // Clear the cachedDataDisplayed flag to allow fresh API calls
      this.cachedDataDisplayed = false;

      // Clear products result to show loading state
      this.productsResult = [];
      this.isLoading.results = true;

      console.log("✅ Cache cleared - fresh API call will be made");
    },

    extractMinMaxPrices(products) {
      if (!Array.isArray(products) || products.length === 0) {
        return { maxPrice: null, minPrice: null };
      }

      let maxPrice = parseFloat(products[0].price);
      let minPrice = parseFloat(products[0].price);

      for (const product of products) {
        const price = parseFloat(product.price);
        if (!isNaN(price)) {
          if (price > maxPrice) {
            maxPrice = price;
          }
          if (price < minPrice) {
            minPrice = price;
          }
        }
      }

      return { maxPrice, minPrice };
    },
    debounce(callback, timer = 1, immediate = true) {
      if (typeof callback === "function")
        return (...args) => {
          if (immediate) {
            return callback(...args);
          } else {
            clearTimeout(this.timeoutVal);
            this.timeoutVal = setTimeout(() => {
              return callback(...args);
            }, Number(timer));
          }
        };
    },

    // sendQueryToClient(){//called at changes at updated
    //   let queryToClientObj={
    //     ...(
    //     this.searchInpt&&
    //     {'search':encodeURIComponent(this.searchInpt)}
    //     ),

    //     ...(
    //     this.subCategory?.selected&&
    //     {'sub_category_id':this.subCategory?.selected?.id}
    //     ),
    //     ...(
    //       this.stores?.selected&&
    //     {'store':JSON.stringify({id:this.stores?.selected.id,name:this.stores?.selected?.name})}
    //     ),
    //     // ...(
    //     //   this.mainAtrrs.length&&
    //     //   {'main_attribute_ids':this.mainAtrrs.map((mainAttr) => mainAttr?.id).toString()}
    //     // ),
    //     ...(
    //       this.subAttrsSelected&&this.subAttrsSelected?.length&&
    //       {'sub_attribute_ids':JSON.stringify(
    //           this.subAttrsSelected.map((array)=>(
    //             array?array.map(item=>item?.id):null//return array of ids
    //             )
    //           )
    //         )
    //     }
    //     ),
    //     ...(
    //       this.secondMainAttrs.length&&
    //       {'second_main_attribute_ids':this.secondMainAttrs.map((secondMainAttr) => secondMainAttr?.id).toString()}
    //     ),

    //     ...((this.space?.from!=='' && this.space?.to!=='') && {
    //       'min_space': this.space.from,
    //       'max_space': this.space.to
    //     }),
    //     'sort_by':this.sortBy,
    //     'sort_type':this.date?.selected.type||'desc',

    //   }
    //   let queryToClientStr=Object.entries(queryToClientObj).map((item,i)=>i==0?item.join('='):`&${item.join('=')}`).join('')//new URLSearchParams(queryToClientObj).toString()
    //   let encodedQuery = btoa(encodeURIComponent(queryToClientStr));
    //   this.$router.replace(`/products${'?' + encodedQuery}`, { silent: true });

    // },
    async getAndInjectQuery() {
      //called when mounted
      try {
        let queryObj = this.decodeQueryObj;
        //--------------------------------------------------
        this.searchInpt = queryObj?.search || "";

        this.subCategory.selected =
          this.subCategory?.options?.find(
            (option) => option.id == queryObj.category_id
          ) || null;

        //first level of attr
        this.mainAtrrs = this.subCategory?.selected?.attributes || [];
        let subAttrsOpts = this.mainAtrrs.map((mainAtrr) => mainAtrr.children);

        let sub_attr_ids_arrs = [];
        if (queryObj?.page == "home-chips") {
          //by mainAttr id  replace selected sub attr in right place in subAttrs options
          this.mainAtrrs.some((item) => {
            if (queryObj.selected_mainAttrId != item.id) {
              sub_attr_ids_arrs.push(null);
              return false;
            } else {
              sub_attr_ids_arrs.push(
                JSON.parse(queryObj?.sub_attribute_ids || "[]")[0]
              );
              return true;
            }
          });
        } else {
          sub_attr_ids_arrs = JSON.parse(queryObj?.sub_attribute_ids || "[]");
        }

        this.subAttrsSelected = sub_attr_ids_arrs.map((nest_ids_arr, indx) =>
          nest_ids_arr
            ? nest_ids_arr.map((id) =>
                subAttrsOpts[indx].find((opt) => opt?.id == id)
              )
            : null
        );

        //second level of attr queryObj?.second_sub_attribute_ids
        this.secondMainAttrs = this.subAttrsSelected
          .filter((item) => !(item == undefined || item == null)) //clear subAttrsSelected from any null or undefined item
          .flat() //[[{}, {}],[{}, {}]] --> [{},{},{},{}]
          .map((item) => item?.attributes || [])
          .flat();

        let secondSubAttrsOpts = this.secondMainAttrs.map(
          (secondMainAtrr) => secondMainAtrr.children
        );

        let second_sub_attr_ids_arrs = JSON.parse(
          queryObj?.second_sub_attribute_ids || "[]"
        );

        this.secondSubAttrsSelected = second_sub_attr_ids_arrs.map(
          (nest_ids_arr, indx) =>
            nest_ids_arr
              ? nest_ids_arr.map((id) =>
                  secondSubAttrsOpts[indx].find((opt) => opt?.id == id)
                )
              : null
        );

        //need get stores api
        this.stores.selected =
          (queryObj?.store && JSON.parse(queryObj?.store)) || null;

        this.date.selected =
          this.date?.options.find(
            (option) => option.type == queryObj?.sort_type
          ) || this.date?.options[0];

        this.rangeValue = {
          min: queryObj?.min_price || this.rangeValue.min,
          max: queryObj?.max_price || this.rangeValue.max,
        };

        queryObj?.min_space != undefined
          ? (this.space = {
              ...this.space,
              from: queryObj?.min_space,
              to: queryObj?.max_space,
            })
          : "";

        this.isNegotiable = queryObj?.is_negotiable
          ? queryObj?.is_negotiable === "true"
            ? true
            : false
          : this.isNegotiable;
      } catch (err) {
        // console.error(err);
      }
    },

    // Debug method to test cache
    testCache() {
      console.log("🧪 Testing cache system...");

      // Test basic cache functionality
      const debugResult = this.debugCache("test_products_page");
      console.log("Debug result:", debugResult);

      // Test current cache state
      const cacheKey = this.generateCacheKey(
        this.getCurrentFilters(),
        this.sortBy,
        null
      );
      console.log("Current cache key:", cacheKey);

      const hasCache = this.hasValidCache(
        cacheKey,
        1,
        this.getCurrentFilters()
      );
      console.log("Has cache for current state?", hasCache);

      if (hasCache) {
        const cacheInfo = this.getCacheInfo(
          cacheKey,
          1,
          this.getCurrentFilters()
        );
        console.log("Cache info:", cacheInfo);
      }

      // Show all cache keys
      const allCache = this.getCache();
      console.log("All cache keys:", Object.keys(allCache));

      // Test cache stats
      const stats = this.getCacheStats();
      console.log("Cache stats:", stats);
    },

    // Manual cache clear method
    manualClearCache() {
      console.log("🗑️ Manually clearing cache...");
      const result = this.manualClearCache();
      if (result) {
        console.log("✅ Cache cleared successfully");
        // Refresh the page to see the effect
        this.$nextTick(() => {
          this.getResults();
        });
      } else {
        console.log("❌ Failed to clear cache");
      }
    },

    // Test product card restoration
    testProductCardRestoration() {
      console.log("🎯 Testing product card restoration...");

      // Get the first product ID from the current list
      if (this.productsResult && this.productsResult.length > 0) {
        const firstProductId = this.productsResult[0].id;
        console.log("Testing restoration to product:", firstProductId);

        // Try to restore to this product
        const restored = this.restoreToProductCard(firstProductId);
        if (restored) {
          console.log("✅ Product card restoration test successful");
        } else {
          console.log("❌ Product card restoration test failed");
        }
      } else {
        console.log("❌ No products available for testing");
      }
    },

    // Test browser reload detection
    testBrowserReloadDetection() {
      console.log("🔄 Testing browser reload detection...");

      try {
        // Test the new detection method
        const isReload = this.detectBrowserReload();
        console.log("Browser reload detected:", isReload);

        if (isReload) {
          console.log(
            "✅ Browser reload detection working - cache will be cleared"
          );
        } else {
          console.log(
            "ℹ️ No browser reload detected - cache will be preserved"
          );
        }

        // Also test the improved hard refresh detection
        this.detectHardRefreshImproved();
      } catch (error) {
        console.error("❌ Browser reload detection test failed:", error);
      }
    },

    // Test page readiness for scroll restoration
    testPageReadiness() {
      console.log("📋 Testing page readiness for scroll restoration...");
      const isReady = this.isPageReadyForScroll();
      console.log("Page ready for scroll restoration:", isReady);
      if (isReady) {
        console.log("✅ Page is ready for scroll restoration");
      } else {
        console.log("❌ Page is NOT ready for scroll restoration");
      }
    },

    // Check if the page is ready for scroll restoration
    isPageReadyForScroll() {
      if (typeof window === "undefined") {
        return false;
      }

      try {
        // Check if document is ready
        if (document.readyState !== "complete") {
          return false;
        }

        // Check if there are products visible on the page
        const productElements = document.querySelectorAll("[data-product-id]");
        if (productElements.length === 0) {
          return false;
        }

        // Check if the page has loaded enough content
        const bodyHeight = document.body.scrollHeight;
        if (bodyHeight < 500) {
          return false;
        }

        return true;
      } catch (error) {
        console.warn("Error checking if page is ready for scroll:", error);
        return false;
      }
    },

    // Check products cache state
    checkProductsCache() {
      console.log("🔍 Checking Products Cache State...");
      console.log("Current cachedDataDisplayed:", this.cachedDataDisplayed);
      console.log("Current productsResult:", this.productsResult);
      console.log("Current currentPage:", this.currentPage);
      console.log("Current allowToLoadMore:", this.allowToLoadMore);
      console.log("Current isLoading.results:", this.isLoading.results);
      console.log("Current isLoading.more:", this.isLoading.more);

      // Check saved products page data
      console.log("🔍 Saved Products Page Data:");
      console.log("Saved data:", this.savedProductspageData);
      console.log("Has saved data?", !!this.savedProductspageData);
      console.log(
        "Saved products count:",
        this.savedProductspageData?.productsResult?.length || 0
      );

      // Check cache system state
      const currentFilters = this.getCurrentFilters();
      const cacheKey = safeProductListCache.generateCacheKey(
        currentFilters,
        this.sortBy || "date",
        null
      );
      console.log("Current cache key:", cacheKey);

      const hasCache = safeProductListCache.hasValidCache(
        cacheKey,
        1,
        currentFilters
      );
      console.log("Has cache for current state?", hasCache);

      if (hasCache) {
        const cacheInfo = safeProductListCache.getCacheInfo(
          cacheKey,
          1,
          currentFilters
        );
        console.log("Cache info:", cacheInfo);
      }

      // Show all cache keys
      const allCache = safeProductListCache.getCache();
      console.log("All cache keys:", Object.keys(allCache));

      // Test cache stats
      const stats = safeProductListCache.getCacheStats();
      console.log("Cache stats:", stats);
    },

    // Save product card position when clicked for precise restoration
    saveProductCardPosition(productId) {
      if (!this.safeProductListCache) return;

      try {
        // CRITICAL: Capture scroll position IMMEDIATELY before any navigation or DOM changes
        const currentScrollX = window.scrollX || window.pageXOffset || 0;
        const currentScrollY = window.scrollY || window.pageYOffset || 0;

        // Get current cache key and page
        const currentFilters = this.getCurrentFilters();
        const cacheKey = this.safeProductListCache.generateCacheKey(
          currentFilters,
          this.sortBy,
          null
        );
        const currentPage = this.currentPage || 1;

        // Get the product card element
        const productCard = document.querySelector(
          `[data-product-id="${productId}"]`
        );
        if (!productCard) {
          console.log(`❌ Product card element not found for ID: ${productId}`);
          return;
        }

        // Get the product card position relative to the viewport
        // Use the captured scroll position, not window.scrollX/Y which might have changed
        const cardRect = productCard.getBoundingClientRect();
        const absoluteY = cardRect.top + currentScrollY;
        const absoluteX = cardRect.left + currentScrollX;
        
        // Validate that we have a valid position (not 0,0 unless product is actually at top)
        // If scroll is 0 and card top is negative or very small, it might be invalid
        if (currentScrollY === 0 && cardRect.top < -100) {
          console.warn(`⚠️ Suspicious position detected for product ${productId}: scrollY=0 but card top=${cardRect.top}. Skipping save.`);
          return;
        }
        
        const cardPosition = {
          x: absoluteX,
          y: absoluteY,
          pageScrollX: currentScrollX,
          pageScrollY: currentScrollY,
          cardHeight: cardRect.height,
          cardWidth: cardRect.width,
        };

        // Save the product card position
        const saved = this.safeProductListCache.saveProductCardPosition(
          cacheKey,
          currentPage,
          productId,
          cardPosition
        );

        if (saved) {
          console.log(
            `📍 Product card position saved for ${productId}:`,
            cardPosition
          );
          
          // Store product ID and context for restoration when returning from product page
          this.storeProductNavigationContext(productId, cacheKey, currentPage);
        }
      } catch (error) {
        console.error("Failed to save product card position:", error);
      }
    },

    // Store product navigation context for restoration (delegates to restoration manager)
    storeProductNavigationContext(productId, cacheKey, page) {
      // Store current filter queries for restoration
      const filterQueries = {
        filterQuery: this.filterQuery || '',
        attributesQuery: this.attributesQuery || '',
        categoryId: this.categoryId || '',
        sortBy: this.sortBy || 'date',
        sortType: this.sortType || 'desc',
        search: this.searchInpt || '',
      };
      this.restorationManager.storeProductNavigationContext(productId, cacheKey, page, 'products', filterQueries);
    },

    // Get stored product navigation context (delegates to restoration manager)
    getStoredProductNavigationContext() {
      return this.restorationManager.getStoredProductNavigationContext();
    },

    // Clear stored product navigation context (delegates to restoration manager)
    clearStoredProductNavigationContext() {
      this.restorationManager.clearStoredProductNavigationContext();
    },

    // Try to restore product position after page loads
    // Strategy: Load the correct page first, then restore position after pagination completes
    tryRestoreProductPosition() {
      if (typeof window === 'undefined' || !this.safeProductListCache) return;
      
      // Check if restoration should be attempted
      if (!this.restorationManager.shouldAttemptRestoration('products')) {
        return;
      }
      
      // Prevent multiple restoration attempts
      if (this.restorationManager.isRestoring.value) {
        console.log('⏸️ Restoration already in progress, skipping duplicate attempt');
        return;
      }
      
      // Wait for filters to be fully initialized before attempting restoration
      // Add max retry limit to prevent infinite loops
      const maxRetries = 20; // Max 6 seconds (20 * 300ms)
      const retryCount = this._restorationRetryCount || 0;
      
      if (this.isInitializingFilters || this.filterManager.isInitializing.value) {
        if (retryCount >= maxRetries) {
          console.warn('⚠️ Max restoration retries reached, proceeding anyway');
          this._restorationRetryCount = 0;
          // Proceed with restoration even if filters aren't fully initialized
        } else {
          this._restorationRetryCount = retryCount + 1;
          console.log(`⏳ Filters still initializing (retry ${retryCount + 1}/${maxRetries}), will retry restoration`);
          setTimeout(() => {
            this.tryRestoreProductPosition();
          }, 300);
          return;
        }
      } else {
        // Reset retry count when filters are ready
        this._restorationRetryCount = 0;
      }
      
      const context = this.restorationManager.getStoredProductNavigationContext();
      if (!context) {
        console.log('🔍 No product navigation context found');
        return;
      }
      
      console.log('🔍 Found product navigation context:', context);
      
      // CRITICAL: Restore filter queries FIRST before verifying cache key
      // This ensures filters match the context's cache key
      if (context.filterQuery !== undefined || context.attributesQuery !== undefined || context.categoryId !== undefined) {
        console.log('🔄 Restoring filter queries from context:', {
          filterQuery: context.filterQuery,
          attributesQuery: context.attributesQuery,
          categoryId: context.categoryId,
          sortBy: context.sortBy,
          sortType: context.sortType,
          search: context.search,
        });
        
        // Restore filter queries via filter manager
        const filterUpdates = {};
        
        if (context.categoryId && context.categoryId !== '0') {
          filterUpdates.category_id = context.categoryId;
        }
        if (context.attributesQuery) {
          const attributeIds = context.attributesQuery.split(',').filter(id => id.trim());
          filterUpdates.attribute_ids = attributeIds;
        }
        if (context.sortBy && context.sortBy !== 'date') {
          filterUpdates.sort_by = context.sortBy;
          filterUpdates.userHasSelectedSorting = true;
        }
        if (context.sortType && context.sortType !== 'desc') {
          filterUpdates.sort_type = context.sortType;
        }
        if (context.search) {
          filterUpdates.search = context.search;
          this.searchInpt = context.search;
        }
        
        // Parse filterQuery string to extract all filter parameters
        if (context.filterQuery) {
          const queryParams = new URLSearchParams(context.filterQuery);
          for (const [key, value] of queryParams.entries()) {
            if (key === 'attribute_ids[]') {
              if (!filterUpdates.attribute_ids) filterUpdates.attribute_ids = [];
              filterUpdates.attribute_ids.push(value);
            } else if (key === 'category_id' && !filterUpdates.category_id) {
              filterUpdates.category_id = value;
            } else if (key === 'min_price') {
              filterUpdates.min_price = parseInt(value, 10);
            } else if (key === 'max_price') {
              filterUpdates.max_price = parseInt(value, 10);
            } else if (key === 'min_space') {
              filterUpdates.min_space = parseInt(value, 10);
            } else if (key === 'max_space') {
              filterUpdates.max_space = parseInt(value, 10);
            } else if (key === 'is_negotiable') {
              filterUpdates.is_negotiable = value === '1' || value === 'true';
            } else if (key === 'sort_by' && !filterUpdates.sort_by) {
              filterUpdates.sort_by = value;
              filterUpdates.userHasSelectedSorting = true;
            } else if (key === 'sort_type' && !filterUpdates.sort_type) {
              filterUpdates.sort_type = value;
            } else if (key === 'sort_by_force') {
              filterUpdates.sort_by_force = value;
            } else if (key === 'product_type') {
              filterUpdates.product_type = value;
            } else if (key === 'rating') {
              filterUpdates.rating = value;
            } else if (key === 'governorate_id') {
              filterUpdates.governorate_id = value;
            } else if (key === 'area_id') {
              filterUpdates.area_id = value;
            } else if (key === 'store_id') {
              filterUpdates.store_id = value;
            }
          }
        }
        
        // Update filter manager with restored filters (skip notification and URL sync to prevent loops)
        this.filterManager.updateFilterState('restoration', filterUpdates, { 
          skipNotification: true,
          skipUrlSync: true, // Don't sync URL during restoration - preserve existing URL
          allowDuringRestoration: true 
        });
        
        // Update local state for backward compatibility
        this.filterQuery = context.filterQuery || '';
        this.attributesQuery = context.attributesQuery || '';
        this.categoryId = context.categoryId || '0';
        this.sortBy = context.sortBy || 'date';
        this.sortType = context.sortType || 'desc';
        
        console.log('✅ Filter queries restored from context');
        
        // Mark filters as initialized after restoring from context
        this.isInitializingFilters = false;
        this.filterManager.markInitialized();
      }
      
      // Check if products are loaded
      if (!this.productsResult || this.productsResult.length === 0) {
        console.log('⏳ Products not loaded yet, will retry restoration');
        setTimeout(() => {
          this.tryRestoreProductPosition();
        }, 500);
        return;
      }
      
      console.log(`✅ Products loaded: ${this.productsResult.length}`);
      
      // Verify cache key still matches (filters/sorting should match now)
      const currentFilters = this.getCurrentFilters();
      const currentCacheKey = this.safeProductListCache.generateCacheKey(
        currentFilters,
        this.sortBy || "date",
        null
      );
      
      if (!this.restorationManager.verifyCacheKey(currentCacheKey)) {
        console.log('🗑️ Cache key mismatch after filter restoration, clearing stored product context');
        console.log('Expected cache key:', context.cacheKey);
        console.log('Current cache key:', currentCacheKey);
        this.restorationManager.clearStoredProductNavigationContext();
        return;
      }
      
      // Start restoration via restoration manager
      this.restorationManager.startRestoration(context);
      
      // Mark filters as initialized after starting restoration
      this.isInitializingFilters = false;
      this.filterManager.markInitialized();
      
      const savedPage = context.page || 1;
      const currentPage = this.currentPage || 1;
      
      // Strategy: Load the correct page first if needed, then restore position after pagination
      if (savedPage !== currentPage && savedPage > 1) {
        console.log(`📄 Product was on page ${savedPage}, current page is ${currentPage}. Loading page ${savedPage} first...`);
        
        // Set pending restore via restoration manager
        this.restorationManager.setPendingProductRestore(context.productId, savedPage);
        
        // Load pages sequentially until we reach the target page
        this.loadPagesUntilTarget(savedPage, context.productId);
      } else {
        // Already on correct page, just restore position after products are loaded
        this.$nextTick(() => {
          setTimeout(() => {
            this.restoreProductPositionAfterPagination(context.productId, savedPage);
          }, 500);
        });
      }
    },

    // Load pages sequentially until we reach the target page
    loadPagesUntilTarget(targetPage, productId) {
      const currentPage = this.currentPage || 1;
      
      if (currentPage >= targetPage) {
        // We've reached the target page, restoration will happen in API response handler
        console.log(`✅ Reached target page ${targetPage}`);
        return;
      }
      
      // Load next page
      console.log(`📄 Loading page ${currentPage + 1} to reach target page ${targetPage}...`);
      
      this.getResults(
        this.productsResult,
        currentPage + 1,
        true, // isLoadingMore
        false // forceSkipCache
      );
      
      // Continue loading if needed (will be handled in API response)
    },

    // Restore product position after pagination has completed
    restoreProductPositionAfterPagination(productId, page) {
      if (!this.safeProductListCache) {
        this.restorationManager.completeRestoration();
        return;
      }
      
      try {
        console.log(`🎯 Restoring product position for ${productId} on page ${page} after pagination`);
        
        // Verify we're on the correct page
        if (this.currentPage !== page) {
          console.log(`⚠️ Page mismatch: expected ${page}, got ${this.currentPage}. Retrying...`);
          setTimeout(() => {
            this.restoreProductPositionAfterPagination(productId, page);
          }, 500);
          return;
        }
        
        // Check if products list is empty
        if (!this.productsResult || this.productsResult.length === 0) {
          console.log(`⏳ Products list is empty, retrying...`);
          setTimeout(() => {
            this.restoreProductPositionAfterPagination(productId, page);
          }, 500);
          return;
        }
        
        // Verify product still exists in current filtered list
        const productExists = this.productsResult.some(p => p.id === productId);
        if (!productExists) {
          console.log(`❌ Product ${productId} not found in current products list. Clearing context.`);
          this.restorationManager.clearStoredProductNavigationContext();
          this.restorationManager.completeRestoration();
          return;
        }
        
        // Check if product exists in DOM
        const productElement = document.querySelector(`[data-product-id="${productId}"]`);
        if (!productElement) {
          console.log(`❌ Product ${productId} not found in DOM. Products may still be loading.`);
          // Retry after delay (max 3 attempts)
          const retryCount = this.restorationManager.getRetryCount();
          if (retryCount < 3) {
            this.restorationManager.incrementRetryCount();
            setTimeout(() => {
              this.restoreProductPositionAfterPagination(productId, page);
            }, 500);
          } else {
            console.log(`❌ Max retries reached. Clearing context.`);
            this.restorationManager.clearStoredProductNavigationContext();
            this.restorationManager.completeRestoration();
            this.restorationManager.resetRetryCount();
          }
          return;
        }
        
        // Reset retry count
        this.restorationManager.resetRetryCount();
        
        // Now restore the position
        const restored = this.restoreToProductCard(productId);
        
        if (restored) {
          console.log(`✅ Successfully restored to product ${productId}`);
          
          // Complete restoration via restoration manager
          this.restorationManager.completeRestoration();
          
          // Restore attributes if pending
          const pendingAttributes = this.restorationManager.restorationState.pendingAttributesRestore;
          if (pendingAttributes) {
            console.log('🔄 Restoring saved attributes after FilterBar initialization:', pendingAttributes);
            setTimeout(() => {
              const attributeIds = pendingAttributes
                .split(',')
                .map(id => id.trim())
                .filter(id => id)
                .map(id => parseInt(id, 10))
                .filter(id => !isNaN(id));
              
              const filterBar = this.$refs.filterBarDesktop || this.$refs.filterBarMobile;
              if (filterBar && attributeIds.length > 0) {
                filterBar.restoreSelectedAttributes(attributeIds);
              }
              this.restorationManager.clearPendingAttributesRestore();
            }, 500);
          }
        } else {
          console.log(`❌ Failed to restore product position. Clearing context.`);
          this.restorationManager.clearStoredProductNavigationContext();
          this.restorationManager.completeRestoration();
        }
      } catch (error) {
        console.error('Error in restoreProductPositionAfterPagination:', error);
        this.restorationManager.clearStoredProductNavigationContext();
        this.restorationManager.completeRestoration();
        this.restorationManager.resetRetryCount();
      }
    },

    // Restore scroll to a specific product card
    restoreToProductCard(productId) {
      if (!this.safeProductListCache) return false;

      try {
        // Get current cache key and page
        const currentFilters = this.getCurrentFilters();
        const cacheKey = this.safeProductListCache.generateCacheKey(
          currentFilters,
          this.sortBy,
          null
        );
        const currentPage = this.currentPage || 1;

        // Restore to the specific product card
        const restored = this.safeProductListCache.restoreToProductCard(
          cacheKey,
          currentPage,
          productId
        );

        if (restored) {
          console.log(`🎯 Scroll restored to product card: ${productId}`);
          // Clear the stored context after successful restoration
          this.clearStoredProductNavigationContext();
        } else {
          console.log(
            `❌ Failed to restore scroll to product card: ${productId}`
          );
        }

        return restored;
      } catch (error) {
        console.error("Failed to restore to product card:", error);
        return false;
      }
    },

    // Navigation detection methods
    markAsNavigation() {
      this.useLocalStorage({ type: "set", key: "isNavigation", value: true });
      // Also mark in cache system
      if (safeProductListCache) {
        safeProductListCache.markAsNavigation();
      }
    },

    setupNavigationDetection() {
      // Use the cache system's navigation detection
      if (safeProductListCache) {
        safeProductListCache.setupNavigationDetection();
      }
    },

    removeNavigationDetection() {
      // Use the cache system's navigation detection
      if (safeProductListCache) {
        safeProductListCache.removeNavigationDetection();
      }
    },

    // Check if we're coming from navigation (back/forward) vs. filter update
    isNavigationFromBrowser() {
      // Check if we have navigation state in sessionStorage
      const navigationState = sessionStorage.getItem("navigationType");
      return navigationState === "navigation";
    },

    // Check if we should use cache for current request
    shouldUseCacheForRequest(updateMode, loadMore) {
      // Always try cache if not in update mode
      if (!updateMode) {
        return true;
      }

      // In update mode, only try cache if:
      // 1. We're coming from browser navigation
      // 2. We don't have products currently displayed
      // 3. This is not a load more request
      return (
        this.isNavigationFromBrowser() &&
        this.productsResult?.length === 0 &&
        !loadMore
      );
    },

    // Clear saved products page data when filters change
    clearSavedProductsData() {
      this.global.updateProductspageData({});
      console.log("🗑️ Cleared saved products page data due to filter change");
    },

    // Update route query parameters - now delegates to filter manager
    updateRouteQueryParams() {
      if (process.server || !this.isMounted) return;
      
      // Filter manager handles URL sync automatically
      // This method is kept for backward compatibility but delegates to manager
      this.filterManager.syncToUrl();
    },

    // Restore scroll position when returning from product page
    restoreScrollPosition() {
      // Priority 1: Product card position restoration (highest priority)
      const productContext = this.getStoredProductNavigationContext();
      if (productContext && productContext.portalPage === 'products') {
        // Prevent package restoration during product restoration
        if (safeProductListCache) {
          safeProductListCache.preventScrollInterference(5000); // 5 seconds
          safeProductListCache.disableAutoScrolling(5000);
        }
        
        // Use product card restoration system
        // Wait for products to be loaded before attempting restoration
        if (this.productsResult && this.productsResult.length > 0) {
          this.$nextTick(() => {
            setTimeout(() => {
              this.tryRestoreProductPosition();
            }, 100); // Shorter delay since we're preventing interference
            });
        } else {
          // Wait for products to load
          this.$nextTick(() => {
            setTimeout(() => {
              this.tryRestoreProductPosition();
            }, 1000);
          });
        }
        return;
      }
      
      // Priority 2: Generic scroll restoration via nuxt-scroll-restoration package
      // Only use if no product context exists
      // Otherwise, let nuxt-scroll-restoration package handle it
      this.$nextTick(() => {
        setTimeout(() => {
          if (process.client) {
            // Check for legacy scroll position in sessionStorage
            const savedScrollPosition = sessionStorage.getItem('scrollPosition_products');
            
            if (savedScrollPosition && !isNaN(savedScrollPosition)) {
              const scrollPosition = parseInt(savedScrollPosition);
              
              // Smooth scroll to the saved position
              window.scrollTo({
                top: scrollPosition,
                behavior: 'smooth'
              });
              
              // Clear the saved position after restoring
              sessionStorage.removeItem('scrollPosition_products');
            } else {
              // No legacy position - let nuxt-scroll-restoration package handle it
              console.log('📦 No legacy position, using nuxt-scroll-restoration package');
            }
          }
        }, 100); // Small delay to ensure DOM is fully rendered
      });
    },
  },
};
</script>

<style lang="scss">
#productsPage {
  .main-attr-container {
    width: 100%;
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    justify-items: start;
    grid-gap: 0.9em;
  }
  .multiselect {
    width: 100%;
  }
  .multiselect__select::before {
    border-color: #0963ff transparent transparent transparent !important;
  }
  .multiselect__tags.multiselect__tags,
  .multiselect__single.multiselect__single {
    background: #ffffff;
  }
  .main-attr-container .multiselect__tags.multiselect__tags,
  .main-attr-container .multiselect__single.multiselect__single {
    background: #f3f3f3;
  }

  .multiselect__single.multiselect__single {
    font-size: clamp(0.8rem, 90%, 1.1rem);
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }

  /*---------------multiselect-date----------------------------*/
  .multiselect-date {
    font-size: clamp(0.4rem, 80%, 1rem);
  }
  .multiselect-date .multiselect__single {
    font-size: clamp(0.4rem, 80%, 1rem);
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }
  .multiselect-date .multiselect__select::before {
    border-color: #0963ff transparent transparent transparent !important;
  }
  @media (min-width: 1024px) {
    .multiselect-date {
      font-size: clamp(0.4rem, 90%, 1rem);
    }
    .multiselect-date .multiselect__single {
      font-size: clamp(0.4rem, 90%, 1rem);
    }
  }
}
</style>
